<template>
    <Modal size="xxl" offset-class="mt-[160px]" @close="$emit('close')">
        <template #header>
            <div
                data-slot="dialog-header"
                class="flex flex-col gap-2 text-center sm:text-left"
            >
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-4">
                        <div
                            class="flex h-12 w-12 items-center justify-center rounded-[12.8px] border border-blue-100 bg-blue-50"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-shield h-6 w-6 text-blue-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                ></path>
                            </svg>
                        </div>
                        <div>
                            <h2
                                id="radix-:r4:"
                                data-slot="dialog-title"
                                class="mb-2 text-xl font-semibold text-gray-900"
                            >
                                {{ $t("vue.dmarc_report_details") }}
                            </h2>
                            <p
                                id="radix-:r5:"
                                data-slot="dialog-description"
                                class="text-sm text-gray-600"
                            >
                                {{ $t("vue.email_authentication_analysis") }}
                                {{ ip }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #body>
            <div class="p-[22px]">
                <!-- Summary cards -->
                <div
                    class="mb-[22px] grid grid-cols-1 gap-[22px] md:grid-cols-4"
                >
                    <div
                        class="rounded-[12.8px] border border-gray-200 bg-white p-4 text-center"
                    >
                        <div
                            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[12.8px] bg-gray-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-server h-6 w-6 text-gray-600"
                                aria-hidden="true"
                            >
                                <rect
                                    width="20"
                                    height="8"
                                    x="2"
                                    y="2"
                                    rx="2"
                                    ry="2"
                                ></rect>
                                <rect
                                    width="20"
                                    height="8"
                                    x="2"
                                    y="14"
                                    rx="2"
                                    ry="2"
                                ></rect>
                                <line x1="6" x2="6.01" y1="6" y2="6"></line>
                                <line x1="6" x2="6.01" y1="18" y2="18"></line>
                            </svg>
                        </div>
                        <p class="mb-1 text-sm text-gray-600">
                            {{ $t("vue.ip_address") }}
                        </p>
                        <p class="font-mono font-medium text-gray-900">
                            {{ ip }}
                        </p>
                    </div>
                    <div
                        class="rounded-[12.8px] border border-gray-200 bg-white p-4 text-center"
                    >
                        <div
                            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[12.8px] bg-gray-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-mail h-6 w-6 text-gray-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"
                                ></path>
                                <rect
                                    x="2"
                                    y="4"
                                    width="20"
                                    height="16"
                                    rx="2"
                                ></rect>
                            </svg>
                        </div>
                        <p class="mb-1 text-sm text-gray-600">
                            {{ $t("vue.emails_reported") }}
                        </p>
                        <p class="font-medium text-gray-900">
                            {{
                                new Intl.NumberFormat().format(
                                    detailRow.fail + detailRow.pass,
                                )
                            }}
                        </p>
                    </div>
                    <div
                        class="rounded-[12.8px] border border-gray-200 bg-white p-4 text-center"
                    >
                        <div
                            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[12.8px] bg-gray-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-globe h-6 w-6 text-gray-600"
                                aria-hidden="true"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <path
                                    d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"
                                ></path>
                                <path d="M2 12h20"></path>
                            </svg>
                        </div>
                        <p class="mb-1 text-sm text-gray-600">
                            {{ $t("vue.header_from") }}
                        </p>
                        <p class="font-medium text-gray-900">
                            {{
                                detailRow.identifiers.header_from ||
                                $t("vue.not_available")
                            }}
                        </p>
                    </div>
                    <div
                        v-if="detailRow.fail > 0"
                        class="rounded-[12.8px] border border-gray-200 bg-white p-4 text-center"
                    >
                        <div
                            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[12.8px] bg-red-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-shield h-6 w-6 text-red-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                ></path>
                            </svg>
                        </div>
                        <p class="mb-1 text-sm text-gray-600">
                            {{ $t("vue.dmarc_status") }}
                        </p>
                        <p class="font-medium capitalize text-red-700">
                            {{ $t("vue.fail") }}
                        </p>
                    </div>
                    <div
                        v-else
                        class="rounded-[12.8px] border border-gray-200 bg-white p-4 text-center"
                    >
                        <div
                            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[12.8px] bg-green-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-shield h-6 w-6 text-green-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                ></path>
                            </svg>
                        </div>
                        <p class="mb-1 text-sm text-gray-600">
                            {{ $t("vue.dmarc_status") }}
                        </p>
                        <p class="font-medium capitalize text-green-700">
                            {{ $t("vue.pass") }}
                        </p>
                    </div>
                </div>

                <!-- Domain information -->
                <div class="mb-[22px]">
                    <h3 class="mb-4 text-sm font-semibold text-gray-900">
                        {{ $t("vue.domain_information") }}
                    </h3>
                    <div
                        class="mb-[22px] grid grid-cols-1 gap-[22px] md:grid-cols-2"
                    >
                        <div
                            class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]"
                        >
                            <div class="mb-2 flex items-center gap-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-globe h-5 w-5 text-gray-500"
                                    aria-hidden="true"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path
                                        d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"
                                    ></path>
                                    <path d="M2 12h20"></path>
                                </svg>
                                <span class="font-medium text-gray-700">{{
                                    $t("vue.envelope_from")
                                }}</span>
                            </div>
                            <p class="font-mono text-gray-900">
                                {{
                                    detailRow.identifiers.envelope_from &&
                                    detailRow.identifiers.envelope_from !== "<>"
                                        ? detailRow.identifiers.envelope_from
                                        : $t("vue.envelope_from_not_available")
                                }}
                            </p>
                        </div>
                        <div
                            class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]"
                        >
                            <div class="mb-2 flex items-center gap-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-mail h-5 w-5 text-gray-500"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"
                                    ></path>
                                    <rect
                                        x="2"
                                        y="4"
                                        width="20"
                                        height="16"
                                        rx="2"
                                    ></rect>
                                </svg>
                                <span class="font-medium text-gray-700">{{
                                    $t("vue.envelope_to")
                                }}</span>
                            </div>
                            <p class="font-mono text-gray-900">
                                {{
                                    detailRow.identifiers.envelope_to &&
                                    detailRow.identifiers.envelope_to !== "<>"
                                        ? detailRow.identifiers.envelope_to
                                        : $t("vue.envelope_to_not_available")
                                }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Authentication results -->
                <div>
                    <h3 class="mb-4 text-sm font-semibold text-gray-900">
                        {{ $t("vue.authentication_results") }}
                    </h3>
                    <div class="space-y-[22px]">
                        <!-- DMARC -->
                        <div
                            class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]"
                        >
                            <div
                                class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
                            >
                                <div class="flex items-center gap-3">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="lucide lucide-shield h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                        ></path>
                                    </svg>
                                    <div>
                                        <h4 class="font-medium text-gray-900">
                                            {{ $t("vue.dmarc_authentication") }}
                                        </h4>
                                        <p class="text-sm text-gray-600">
                                            {{
                                                $t(
                                                    "vue.domain_based_message_authentication",
                                                )
                                            }}
                                        </p>
                                    </div>
                                </div>
                                <div :class="dmarcView.badgeClass">
                                    {{
                                        dmarcView.passed
                                            ? $t("vue.pass_status")
                                            : $t("vue.fail_status")
                                    }}
                                </div>
                            </div>

                            <div class="mt-4 rounded-lg bg-gray-50 p-4">
                                <p
                                    class="text-xs font-medium uppercase tracking-wide text-gray-500"
                                >
                                    {{ $t("vue.dmarc_result_reason") }}
                                </p>
                                <p class="mt-1 text-sm text-gray-700">
                                    {{ dmarcView.reason }}
                                </p>
                            </div>
                        </div>

                        <!-- SPF -->
                        <div
                            class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]"
                        >
                            <div
                                class="mb-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
                            >
                                <div class="flex items-center gap-3">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="lucide lucide-shield-check h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                        ></path>
                                        <path d="m9 12 2 2 4-4"></path>
                                    </svg>
                                    <div>
                                        <h4 class="font-medium text-gray-900">
                                            {{ $t("vue.spf_authentication") }}
                                        </h4>
                                        <p class="text-sm text-gray-600">
                                            {{
                                                $t(
                                                    "vue.spf_authentication_description",
                                                )
                                            }}
                                        </p>
                                    </div>
                                </div>
                                <div :class="spfView.badgeClass">
                                    {{ spfView.badgeLabel }}
                                </div>
                            </div>

                            <div class="pl-0 md:pl-9">
                                <!-- Aggregate detail: always shown -->
                                <div
                                    class="grid grid-cols-1 gap-3 md:grid-cols-3"
                                >
                                    <div class="rounded-lg bg-gray-50 p-3">
                                        <p
                                            class="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500"
                                        >
                                            {{
                                                $t("vue.authentication_result")
                                            }}
                                        </p>
                                        <p
                                            :class="spfView.resultClass"
                                            class="text-sm font-semibold capitalize"
                                        >
                                            {{ spfView.resultLabel }}
                                        </p>
                                    </div>
                                    <div class="rounded-lg bg-gray-50 p-3">
                                        <p
                                            class="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500"
                                        >
                                            {{
                                                $t("vue.header_from_alignment")
                                            }}
                                        </p>
                                        <p
                                            :class="spfView.alignmentClass"
                                            class="text-sm font-semibold"
                                        >
                                            {{ spfView.alignmentLabel }}
                                        </p>
                                    </div>
                                    <div class="rounded-lg bg-gray-50 p-3">
                                        <p
                                            class="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500"
                                        >
                                            {{ $t("vue.spf_domain") }}
                                        </p>
                                        <div
                                            v-if="spfView.domains.length > 0"
                                            class="space-y-1"
                                        >
                                            <p
                                                v-for="domain in spfView.domains"
                                                :key="domain"
                                                class="font-mono text-sm text-gray-900"
                                            >
                                                {{ domain }}
                                            </p>
                                        </div>
                                        <p
                                            v-else
                                            class="font-mono text-sm text-gray-400"
                                        >
                                            {{
                                                $t(
                                                    "vue.spf_domain_not_available",
                                                )
                                            }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Per-record breakdown (multiple SPF entries) -->
                                <div
                                    v-if="spfView.hasMultipleRecords"
                                    class="mt-3 border-t border-gray-100 pt-3"
                                >
                                    <p
                                        class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ $t("vue.spf_domain") }}
                                    </p>
                                    <div class="space-y-2">
                                        <div
                                            v-for="record in spfView.records"
                                            :key="record.domain + record.result"
                                            class="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 px-3 py-2 text-sm"
                                        >
                                            <span
                                                class="min-w-0 flex-1 font-mono text-gray-700"
                                                >{{
                                                    record.domain ||
                                                    $t("vue.not_available")
                                                }}</span
                                            >
                                            <span
                                                :class="record.resultClass"
                                                class="font-medium capitalize"
                                                >{{ record.resultLabel }}</span
                                            >
                                            <span
                                                :class="record.alignedClass"
                                                class="font-medium"
                                                >{{ record.alignedLabel }}</span
                                            >
                                            <span
                                                v-if="record.usedForDmarc"
                                                class="text-xs font-semibold text-green-700"
                                                >✓ DMARC</span
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- DKIM -->
                        <div
                            class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]"
                        >
                            <div
                                class="mb-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
                            >
                                <div class="flex items-center gap-3">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="lucide lucide-shield-check h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                        ></path>
                                        <path d="m9 12 2 2 4-4"></path>
                                    </svg>
                                    <div>
                                        <h4 class="font-medium text-gray-900">
                                            {{ $t("vue.dkim_authentication") }}
                                        </h4>
                                        <p class="text-sm text-gray-600">
                                            {{
                                                $t(
                                                    "vue.dkim_authentication_description",
                                                )
                                            }}
                                        </p>
                                    </div>
                                </div>
                                <div :class="dkimView.badgeClass">
                                    {{ dkimView.badgeLabel }}
                                </div>
                            </div>

                            <div class="pl-0 md:pl-9">
                                <!-- Aggregate detail: shown only for single record -->
                                <div
                                    v-if="!dkimView.hasMultipleRecords"
                                    class="grid grid-cols-1 gap-3 md:grid-cols-3"
                                >
                                    <div class="rounded-lg bg-gray-50 p-3">
                                        <p
                                            class="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500"
                                        >
                                            {{
                                                $t("vue.authentication_result")
                                            }}
                                        </p>
                                        <p
                                            :class="dkimView.resultClass"
                                            class="text-sm font-semibold capitalize"
                                        >
                                            {{ dkimView.resultLabel }}
                                        </p>
                                    </div>
                                    <div class="rounded-lg bg-gray-50 p-3">
                                        <p
                                            class="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500"
                                        >
                                            {{
                                                $t("vue.header_from_alignment")
                                            }}
                                        </p>
                                        <p
                                            :class="dkimView.alignmentClass"
                                            class="text-sm font-semibold"
                                        >
                                            {{ dkimView.alignmentLabel }}
                                        </p>
                                    </div>
                                    <div class="rounded-lg bg-gray-50 p-3">
                                        <p
                                            class="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500"
                                        >
                                            {{ $t("vue.dkim_domains") }}
                                        </p>
                                        <div
                                            v-if="dkimView.domains.length > 0"
                                            class="space-y-1"
                                        >
                                            <p
                                                v-for="domain in dkimView.domains"
                                                :key="domain"
                                                class="font-mono text-sm text-gray-900"
                                            >
                                                {{ domain }}
                                            </p>
                                        </div>
                                        <p
                                            v-else
                                            class="font-mono text-sm text-gray-400"
                                        >
                                            {{
                                                $t(
                                                    "vue.dkim_domain_not_available",
                                                )
                                            }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Per-record breakdown (multiple DKIM signatures) -->
                                <div v-if="dkimView.hasMultipleRecords">
                                    <p
                                        class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ $t("vue.dkim_signatures") }}
                                    </p>
                                    <div class="space-y-2">
                                        <div
                                            v-for="record in dkimView.records"
                                            :key="record.domain + record.result"
                                            class="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 px-3 py-2 text-sm"
                                        >
                                            <span
                                                class="min-w-0 flex-1 font-mono text-gray-700"
                                                >{{
                                                    record.domain ||
                                                    $t("vue.not_available")
                                                }}</span
                                            >
                                            <span
                                                :class="record.resultClass"
                                                class="font-medium capitalize"
                                                >{{ record.resultLabel }}</span
                                            >
                                            <span
                                                :class="record.alignedClass"
                                                class="font-medium"
                                                >{{ record.alignedLabel }}</span
                                            >
                                            <span
                                                v-if="record.usedForDmarc"
                                                class="text-xs font-semibold text-green-700"
                                                >✓ DMARC</span
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Security assessment -->
                <div
                    v-if="dmarcView.assessment"
                    class="mt-[22px] rounded-[12.8px] border border-blue-200 bg-blue-50 p-[21px]"
                >
                    <div class="flex items-start gap-4">
                        <div
                            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[12.8px] bg-blue-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-shield h-5 w-5 text-blue-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                ></path>
                            </svg>
                        </div>
                        <div>
                            <h4 class="mb-2 font-medium text-gray-900">
                                {{ $t("vue.security_assessment") }}
                            </h4>
                            <p class="text-gray-700">
                                {{ dmarcView.assessment }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between border-t border-gray-200 p-[22px]">
                <AtomicButton
                    size="md"
                    color="yellow"
                    data-cy="create-token-finish"
                    @click="$emit('close')"
                >
                    {{ $t("vue.close") }}
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts" setup>
import Modal from "@/components/Atomic/Modals/Modal.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import { computed } from "vue";
import { useTranslations } from "@/composables/useTranslations.ts";

const { t } = useTranslations();

type AuthResult = {
    result?: string;
    domain?: string;
};

type AuthRecord = {
    domain: string;
    result: string;
    aligned: boolean;
    usedForDmarc: boolean;
};

type RecordView = AuthRecord & {
    resultClass: string;
    resultLabel: string;
    alignedLabel: string;
    alignedClass: string;
};

type MechanismResult = {
    result: string;
    aligned: boolean;
    usedForDmarc: boolean;
    domains: string[];
    records: AuthRecord[];
};

type MechanismView = {
    result: string;
    aligned: boolean;
    usedForDmarc: boolean;
    domains: string[];
    records: RecordView[];
    hasMultipleRecords: boolean;
    badgeClass: string;
    badgeLabel: string;
    resultClass: string;
    resultLabel: string;
    alignmentClass: string;
    alignmentLabel: string;
};

type DmarcView = {
    passed: boolean;
    badgeClass: string;
    reason: string;
    assessment: string;
};

type DetailRow = {
    pass: number;
    fail: number;
    identifiers: {
        header_from: string;
        envelope_from?: string;
        envelope_to?: string;
    };
    auth_results: {
        spf?: AuthResult | AuthResult[];
        dkim?: AuthResult | AuthResult[];
    };
};

const props = withDefaults(
    defineProps<{
        detailRow: DetailRow;
        ip?: string;
    }>(),
    {
        ip: "",
    },
);

defineEmits(["close"]);

// --- Pure data helpers ---

const normalizeResults = (input?: AuthResult | AuthResult[]): AuthResult[] => {
    if (!input) return [];
    return Array.isArray(input) ? input : [input];
};

const normalizeDomain = (domain?: string): string =>
    domain?.trim().toLowerCase() ?? "";

const matchesHeaderFrom = (domain?: string): boolean =>
    normalizeDomain(domain) !== "" &&
    normalizeDomain(domain) ===
        normalizeDomain(props.detailRow.identifiers.header_from);

const aggregateResult = (results: AuthResult[]): string => {
    const values = results.map((r) => r.result ?? "");
    if (values.includes("pass")) return "pass";
    if (values.includes("neutral")) return "neutral";
    if (values.includes("temperror")) return "temperror";
    if (values.includes("permerror")) return "permerror";
    if (values.includes("fail")) return "fail";
    return "not available";
};

const buildMechanismResult = (
    input?: AuthResult | AuthResult[],
): MechanismResult => {
    const normalized = normalizeResults(input);
    const domains = [
        ...new Set(
            normalized
                .map((r) => r.domain)
                .filter((d): d is string => Boolean(d)),
        ),
    ];
    const records: AuthRecord[] = normalized.map((r) => {
        const aligned = matchesHeaderFrom(r.domain);
        return {
            domain: r.domain ?? "",
            result: r.result ?? "not available",
            aligned,
            usedForDmarc: r.result === "pass" && aligned,
        };
    });

    return {
        result: aggregateResult(normalized),
        aligned: records.some((r) => r.aligned),
        usedForDmarc: records.some((r) => r.usedForDmarc),
        domains,
        records,
    };
};

// --- Display helpers ---

const formatResult = (result: string): string => result.replaceAll("_", " ");

const statusBadgeClass = (status: string): string => {
    const base =
        "inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium";
    if (status === "pass")
        return `${base} border-green-200 bg-green-50 text-green-700`;
    if (status === "warning" || status === "neutral")
        return `${base} border-yellow-200 bg-yellow-50 text-yellow-700`;
    if (status === "not available" || status === "")
        return `${base} border-gray-200 bg-gray-50 text-gray-500`;
    return `${base} border-red-200 bg-red-50 text-red-700`;
};

const statusTextClass = (status: string): string => {
    if (status === "pass") return "text-green-700";
    if (status === "neutral") return "text-yellow-700";
    if (status === "not available" || status === "") return "text-gray-400";
    return "text-red-700";
};

const alignmentTextClass = (aligned: boolean): string =>
    aligned ? "text-green-700" : "text-red-700";

const mechanismBadgeStatus = (m: MechanismResult): string => {
    if (m.usedForDmarc) return "pass";
    if (m.result === "not available" || m.result === "") return "not available";
    if (m.result === "pass") return "warning";
    return "fail";
};

const mechanismContributionLabel = (m: MechanismResult): string => {
    if (m.usedForDmarc) return t("vue.used_for_dmarc");
    if (m.result === "not available" || m.result === "")
        return t("vue.not_available");
    if (m.result === "pass") return t("vue.not_used_for_dmarc_not_aligned");
    return t("vue.not_used_for_dmarc_auth_failed");
};

// --- Computed data ---

const spfMechanism = computed(
    (): MechanismResult =>
        buildMechanismResult(props.detailRow.auth_results.spf),
);

const dkimMechanism = computed(
    (): MechanismResult =>
        buildMechanismResult(props.detailRow.auth_results.dkim),
);

const dmarcPassed = computed(() => props.detailRow.fail <= 0);

const dmarcReason = computed(() => {
    if (!dmarcPassed.value) return t("vue.dmarc_reason_fail");
    if (spfMechanism.value.usedForDmarc && dkimMechanism.value.usedForDmarc)
        return t("vue.dmarc_reason_both_pass");
    if (spfMechanism.value.usedForDmarc) return t("vue.dmarc_reason_spf_pass");
    if (dkimMechanism.value.usedForDmarc)
        return t("vue.dmarc_reason_dkim_pass");
    return t("vue.dmarc_reason_relaxed_alignment");
});

const dmarcAssessmentNotes = computed((): string[] => {
    const notes: string[] = [];
    if (
        spfMechanism.value.result === "pass" &&
        !spfMechanism.value.usedForDmarc
    ) {
        notes.push(t("vue.dmarc_assessment_spf_not_aligned"));
    }
    if (
        dkimMechanism.value.result === "pass" &&
        !dkimMechanism.value.usedForDmarc
    ) {
        notes.push(t("vue.dmarc_assessment_dkim_not_aligned"));
    }
    return notes;
});

// --- Computed view models ---

const buildRecordView = (r: AuthRecord): RecordView => ({
    ...r,
    resultClass: statusTextClass(r.result),
    resultLabel: formatResult(r.result),
    alignedLabel: r.aligned ? t("vue.aligned") : t("vue.not_aligned"),
    alignedClass: alignmentTextClass(r.aligned),
});

const buildMechanismView = (m: MechanismResult): MechanismView => ({
    result: m.result,
    aligned: m.aligned,
    usedForDmarc: m.usedForDmarc,
    domains: m.domains,
    records: m.records.map(buildRecordView),
    hasMultipleRecords: m.records.length > 1,
    badgeClass: statusBadgeClass(mechanismBadgeStatus(m)),
    badgeLabel: mechanismContributionLabel(m),
    resultClass: statusTextClass(m.result),
    resultLabel: formatResult(m.result),
    alignmentClass: alignmentTextClass(m.aligned),
    alignmentLabel: m.aligned ? t("vue.aligned") : t("vue.not_aligned"),
});

const spfView = computed(
    (): MechanismView => buildMechanismView(spfMechanism.value),
);

const dkimView = computed(
    (): MechanismView => buildMechanismView(dkimMechanism.value),
);

const dmarcView = computed(
    (): DmarcView => ({
        passed: dmarcPassed.value,
        badgeClass: statusBadgeClass(dmarcPassed.value ? "pass" : "fail"),
        reason: dmarcReason.value,
        assessment:
            dmarcAssessmentNotes.value.length > 0
                ? dmarcAssessmentNotes.value.join(" ")
                : dmarcReason.value,
    }),
);
</script>
